import { useState, useEffect, useCallback } from 'react';

export type ConnectionSpeed = 'fast' | 'medium' | 'slow';

interface VideoQualitySettings {
  selectedQuality: string;
  connectionSpeed: ConnectionSpeed;
  autoQuality: boolean;
}

interface UseVideoQualityReturn {
  selectedQuality: string;
  connectionSpeed: ConnectionSpeed;
  autoQuality: boolean;
  setQuality: (quality: string) => void;
  toggleAutoQuality: () => void;
  getRecommendedQuality: () => string;
  detectConnectionSpeed: () => Promise<ConnectionSpeed>;
}

const STORAGE_KEY = 'video_quality_settings';

// Default quality mappings based on connection speed
const QUALITY_MAPPING = {
  fast: 'hd1080',
  medium: 'hd720',
  slow: 'medium'
} as const;

// Connection speed detection thresholds (Mbps)
const SPEED_THRESHOLDS = {
  fast: 5,    // >= 5 Mbps
  medium: 2,  // >= 2 Mbps
  slow: 0     // < 2 Mbps
} as const;

export const useVideoQuality = (): UseVideoQualityReturn => {
  const [settings, setSettings] = useState<VideoQualitySettings>({
    selectedQuality: 'auto',
    connectionSpeed: 'medium',
    autoQuality: true
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedSettings = JSON.parse(saved);
        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }));
      }
    } catch (error) {
      console.warn('Failed to load video quality settings:', error);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save video quality settings:', error);
    }
  }, [settings]);

  // Detect connection speed using Network Information API or fallback methods
  const detectConnectionSpeed = useCallback(async (): Promise<ConnectionSpeed> => {
    try {
      // Try Network Information API first (limited browser support)
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        if (connection) {
          const effectiveType = connection.effectiveType;
          const downlink = connection.downlink; // Mbps
          
          if (downlink >= SPEED_THRESHOLDS.fast || effectiveType === '4g') {
            return 'fast';
          } else if (downlink >= SPEED_THRESHOLDS.medium || effectiveType === '3g') {
            return 'medium';
          } else {
            return 'slow';
          }
        }
      }

      // Fallback: Simple download test
      const startTime = Date.now();
      const testUrl = 'https://www.google.com/favicon.ico?' + Math.random();
      
      try {
        await fetch(testUrl, { mode: 'no-cors', cache: 'no-cache' });
        const duration = Date.now() - startTime;
        
        if (duration < 100) {
          return 'fast';
        } else if (duration < 300) {
          return 'medium';
        } else {
          return 'slow';
        }
      } catch {
        // If fetch fails, assume medium speed
        return 'medium';
      }
    } catch (error) {
      console.warn('Connection speed detection failed:', error);
      return 'medium';
    }
  }, []);

  // Update connection speed on mount and periodically
  useEffect(() => {
    const updateConnectionSpeed = async () => {
      const speed = await detectConnectionSpeed();
      setSettings(prev => ({
        ...prev,
        connectionSpeed: speed
      }));
    };

    // Initial detection
    updateConnectionSpeed();

    // Re-detect every 30 seconds
    const interval = setInterval(updateConnectionSpeed, 30000);

    // Listen for online/offline events
    const handleOnline = () => updateConnectionSpeed();
    const handleOffline = () => {
      setSettings(prev => ({
        ...prev,
        connectionSpeed: 'slow'
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [detectConnectionSpeed]);

  const getRecommendedQuality = useCallback((): string => {
    return QUALITY_MAPPING[settings.connectionSpeed];
  }, [settings.connectionSpeed]);

  const setQuality = useCallback((quality: string) => {
    setSettings(prev => ({
      ...prev,
      selectedQuality: quality,
      autoQuality: quality === 'auto'
    }));
  }, []);

  const toggleAutoQuality = useCallback(() => {
    setSettings(prev => {
      const newAutoQuality = !prev.autoQuality;
      return {
        ...prev,
        autoQuality: newAutoQuality,
        selectedQuality: newAutoQuality ? 'auto' : getRecommendedQuality()
      };
    });
  }, [getRecommendedQuality]);

  // Auto-adjust quality when connection speed changes (if auto is enabled)
  useEffect(() => {
    if (settings.autoQuality) {
      const recommendedQuality = getRecommendedQuality();
      if (settings.selectedQuality !== 'auto' && settings.selectedQuality !== recommendedQuality) {
        setSettings(prev => ({
          ...prev,
          selectedQuality: 'auto'
        }));
      }
    }
  }, [settings.connectionSpeed, settings.autoQuality, getRecommendedQuality, settings.selectedQuality]);

  return {
    selectedQuality: settings.selectedQuality,
    connectionSpeed: settings.connectionSpeed,
    autoQuality: settings.autoQuality,
    setQuality,
    toggleAutoQuality,
    getRecommendedQuality,
    detectConnectionSpeed
  };
};



